/**
 * File Explorer — Issue #557
 * Sourcetrail-style navigation for the import graph.
 * Shows importedBy (who imports this file) and imports (what this file imports).
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import type { ImportGraphData, ImportGraphNode } from './importGraphFlowConverter';
import { ROLE_COLORS } from './importGraphFlowConverter';

interface FileExplorerProps {
  data: ImportGraphData;
  selectedFileId?: string;
  onSelectFile?: (fileId: string) => void;
  onViewSubgraph?: (fileId: string) => void;
}

export function FileExplorer({ data, selectedFileId, onSelectFile, onViewSubgraph }: FileExplorerProps) {
  const nodeMap = useMemo(() => {
    const m = new Map<string, ImportGraphNode>();
    for (const n of data.graph.nodes) m.set(n.id, n);
    return m;
  }, [data.graph.nodes]);

  const defaultId = selectedFileId || data.graph.nodes.find(n => n.isEntryPoint)?.id || data.graph.nodes[0]?.id;
  const [activeId, setActiveId] = useState<string | null>(defaultId ?? null);
  const [history, setHistory] = useState<string[]>([]);
  const [future, setFuture] = useState<string[]>([]);

  useEffect(() => {
    if (selectedFileId && selectedFileId !== activeId) {
      setActiveId(selectedFileId);
    }
  }, [selectedFileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeNode = activeId ? nodeMap.get(activeId) : null;

  const navigateTo = useCallback((fileId: string) => {
    if (activeId) {
      setHistory(h => [...h, activeId]);
      setFuture([]);
    }
    setActiveId(fileId);
    onSelectFile?.(fileId);
  }, [activeId, onSelectFile]);

  const goBack = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    if (activeId) setFuture(f => [...f, activeId]);
    setHistory(h => h.slice(0, -1));
    setActiveId(prev);
  }, [history, activeId]);

  const goForward = useCallback(() => {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    if (activeId) setHistory(h => [...h, activeId]);
    setFuture(f => f.slice(0, -1));
    setActiveId(next);
  }, [future, activeId]);

  const navigateToHistoryIndex = useCallback((i: number) => {
    const target = history[i];
    const tail = history.slice(i + 1).reverse();
    if (activeId) tail.unshift(activeId);
    setFuture(f => [...tail, ...f]);
    setHistory(history.slice(0, i));
    setActiveId(target);
  }, [history, activeId]);

  const roleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Breadcrumb with back/forward buttons */}
      {(history.length > 0 || future.length > 0) && (
        <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1">
          <button onClick={goBack} disabled={history.length === 0}
            className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 flex-shrink-0"
            title="Back">←</button>
          <button onClick={goForward} disabled={future.length === 0}
            className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 flex-shrink-0"
            title="Forward">→</button>
          <div className="flex items-center gap-1 text-sm overflow-x-auto min-w-0 ml-1">
            {history.map((id, i) => (
              <React.Fragment key={`${id}-${i}`}>
                <button onClick={() => navigateToHistoryIndex(i)}
                  className="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[120px] flex-shrink-0"
                  title={nodeMap.get(id)?.filePath}>
                  {nodeMap.get(id)?.name ?? id}
                </button>
                <span className="text-gray-400 flex-shrink-0">›</span>
              </React.Fragment>
            ))}
            <span className="font-medium text-gray-800 dark:text-gray-200 truncate flex-shrink-0">
              {activeNode?.name}
            </span>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-auto p-4">
        {activeNode ? (
          <div className="space-y-6">
            {/* Imported By */}
            <div>
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 uppercase tracking-wide">
                Imported By ({activeNode.importedBy.length})
              </div>
              <div className="space-y-2 ml-4">
                {activeNode.importedBy.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">Not imported by any file (entry point)</p>
                ) : (
                  activeNode.importedBy.slice(0, 20).map(id => {
                    const n = nodeMap.get(id);
                    if (!n) return null;
                    return (
                      <button
                        key={id}
                        onClick={() => navigateTo(id)}
                        className="w-full p-2.5 text-left bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ROLE_COLORS[n.role] }} />
                          <span className="font-medium dark:text-white">{n.name}</span>
                          <span
                            className="px-1.5 py-0.5 text-xs rounded text-white"
                            style={{ backgroundColor: ROLE_COLORS[n.role] }}
                          >
                            {roleLabel(n.role)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 truncate">{n.filePath}</div>
                      </button>
                    );
                  })
                )}
                {activeNode.importedBy.length > 20 && (
                  <p className="text-xs text-gray-400 ml-2">+{activeNode.importedBy.length - 20} more</p>
                )}
              </div>
            </div>

            {/* Active File Card */}
            <div className="border-2 rounded-xl p-6" style={{ borderColor: ROLE_COLORS[activeNode.role] }}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 text-sm rounded text-white" style={{ backgroundColor: ROLE_COLORS[activeNode.role] }}>
                      {roleLabel(activeNode.role)}
                    </span>
                    {activeNode.isEntryPoint && <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">entry point</span>}
                    {activeNode.isBarrel && <span className="px-2 py-0.5 text-xs bg-cyan-100 text-cyan-800 rounded">barrel</span>}
                    {activeNode.hasCircularDep && <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">circular</span>}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{activeNode.name}</h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 break-all">{activeNode.filePath}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                    <div>{activeNode.importedBy.length} importers</div>
                    <div>{activeNode.imports.length} imports</div>
                  </div>
                  {onViewSubgraph && (
                    <button
                      onClick={() => onViewSubgraph(activeNode.id)}
                      className="flex items-center gap-2 px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      View Subgraph
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 flex gap-6 text-sm text-gray-500 dark:text-gray-400">
                <span>Exports: <strong className="text-gray-800 dark:text-white">{activeNode.exportCount}</strong></span>
                <span>Import count: <strong className="text-gray-800 dark:text-white">{activeNode.importCount}</strong></span>
                <span>Layer: <strong className="text-gray-800 dark:text-white">{activeNode.layer}</strong></span>
              </div>
            </div>

            {/* Imports */}
            <div>
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 uppercase tracking-wide">
                Imports ({activeNode.imports.length})
              </div>
              <div className="space-y-2 ml-4">
                {activeNode.imports.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">No imports (leaf file)</p>
                ) : (
                  activeNode.imports.slice(0, 20).map(id => {
                    const n = nodeMap.get(id);
                    if (!n) return (
                      <div key={id} className="p-2 text-xs text-gray-400 font-mono">{id}</div>
                    );
                    return (
                      <button
                        key={id}
                        onClick={() => navigateTo(id)}
                        className="w-full p-2.5 text-left bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ROLE_COLORS[n.role] }} />
                          <span className="font-medium dark:text-white">{n.name}</span>
                          <span
                            className="px-1.5 py-0.5 text-xs rounded text-white"
                            style={{ backgroundColor: ROLE_COLORS[n.role] }}
                          >
                            {roleLabel(n.role)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 truncate">{n.filePath}</div>
                      </button>
                    );
                  })
                )}
                {activeNode.imports.length > 20 && (
                  <p className="text-xs text-gray-400 ml-2">+{activeNode.imports.length - 20} more</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <p>No file selected</p>
            <p className="text-sm mt-2">Click a file in the List or Tree view to start exploring.</p>
          </div>
        )}
      </div>

      {/* Stats footer */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
        {data.graph.nodes.length} files | {data.graph.edges.length} imports | {data.metadata.entryPointCount} entry points
      </div>
    </div>
  );
}
